import React, { useRef, useState } from 'react';
import { Snackbar, Alert, Tooltip, IconButton } from '@mui/material';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import ConfirmPopup from '../../../popup/ConfirmPopup/ConfirmPopup';
import useRecoveryData from './hooks/useRecoveryData';
import OpportunityList from './components/OpportunityList';
import CandidatesDrawer from './components/CandidatesDrawer';
import ContactDrawer from './components/ContactDrawer';
import ResultsPanel from './components/ResultsPanel';
import styles from './Recovery.module.css';

const SLOT_TAKEN_TEXT = 'Esse horário acabou de ser ocupado. Os horários livres foram atualizados.';

/**
 * Recuperar atendimentos (beta do admin da plataforma): horários livres dos
 * próximos dias, quem chamar para cada um, o convite pelo WhatsApp e o
 * resultado medido. A página só orquestra; dados e regras ficam no hook.
 */
function RecoveryPage() {
  const {
    filters, setFilters,
    opportunities, loadOpportunities,
    candidates, loadCandidates,
    composeMessage, sendContact, dismissCandidate,
    actions, loadActions,
    stepMinutes,
  } = useRecoveryData();

  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [contactCandidate, setContactCandidate] = useState(null);
  const [contactError, setContactError] = useState(null);
  const [compose, setCompose] = useState({ status: 'idle', message: '' });
  const composeRequest = useRef(0);
  const [sending, setSending] = useState(false);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (text, severity = 'success') => setToast({ text, severity });

  function handleSelectInterval(interval) {
    loadCandidates(interval);
    setCandidatesOpen(true);
  }

  function handleCloseCandidates() {
    setCandidatesOpen(false);
  }

  // Pede o convite personalizado pela IA; uma resposta atrasada de outra pessoa é ignorada
  function requestCompose(candidate, time, minutes) {
    const interval = candidates.interval;
    if (!interval || !candidate) return;
    const request = ++composeRequest.current;
    setCompose({ status: 'loading', message: '' });
    composeMessage({
      professionalId: interval.professionalId || 0,
      date: interval.date,
      time,
      minutes,
      patientId: candidate.patientId,
      procedureId: candidate.suggestedProcedure?.id || 0,
    }, (result) => {
      if (request !== composeRequest.current) return;
      if (!result) {
        setCompose({ status: 'template', message: '' });
        return;
      }
      setCompose({ status: result.source === 'ai' ? 'ai' : 'template', message: result.message });
    });
  }

  function handleCall(candidate) {
    setContactError(null);
    setContactCandidate(candidate);
    requestCompose(candidate, candidate.suggestedTime, candidate.suggestedProcedure?.minutes || stepMinutes);
  }

  function handleCloseContact() {
    composeRequest.current += 1;
    setContactCandidate(null);
    setCompose({ status: 'idle', message: '' });
  }

  function handleSend({ time, minutes, message, messageSource }) {
    const interval = candidates.interval;
    if (!interval || !contactCandidate) return;
    setSending(true);
    setContactError(null);
    sendContact({
      professionalId: interval.professionalId || 0,
      date: interval.date,
      time,
      minutes,
      patientId: contactCandidate.patientId,
      procedureId: contactCandidate.suggestedProcedure?.id || null,
      message,
      messageSource,
      reasons: (contactCandidate.reasons || []).map((r) => r.code),
    }, (response) => {
      setSending(false);
      if (response?.success) {
        handleCloseContact();
        showToast('Convite enviado para a fila do WhatsApp.');
        return;
      }
      if (response?.code === 'SLOT_TAKEN') {
        // O hook já recarregou os horários; aqui só fecha o fluxo desse intervalo
        handleCloseContact();
        setCandidatesOpen(false);
        showToast(SLOT_TAKEN_TEXT, 'warning');
        return;
      }
      // NOT_ELIGIBLE, DUPLICATE, BLOCKED, INVALID_PARAMS e falhas de rede: o texto da API
      setContactError(response?.error || 'Não foi possível enviar agora. Tente de novo.');
    });
  }

  function handleConfirmDismiss() {
    const candidate = dismissTarget;
    if (!candidate) return;
    dismissCandidate(candidate, (response) => {
      if (response?.success) showToast(`${candidate.name} não aparece nas sugestões por 30 dias.`);
      else showToast(response?.error || 'Não foi possível registrar agora. Tente de novo.', 'error');
    });
  }

  function handleRefresh() {
    loadOpportunities();
    loadActions();
  }

  return (
    <div className="page-content">
      <div className={styles.container}>
        <div className={styles.page}>
          <div className={styles.header}>
            <AreaTitle
              Title="Recuperar atendimentos"
              Icon={<EventRepeatOutlinedIcon />}
              Description="Horários livres dos próximos dias e as pessoas com mais chance de voltar"
            />
            <Tooltip title="Atualizar" arrow>
              <IconButton onClick={handleRefresh} size="small" aria-label="Atualizar">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          <OpportunityList
            opportunities={opportunities}
            filters={filters}
            onFilterChange={setFilters}
            onSelect={handleSelectInterval}
            onRetry={loadOpportunities}
          />

          <ResultsPanel actions={actions} onRetry={loadActions} />
        </div>
      </div>

      {/* Drawers, confirmação e Snackbar fora do container: a contenção do @container prenderia o position: fixed */}
      <CandidatesDrawer
        open={candidatesOpen}
        onClose={handleCloseCandidates}
        candidates={candidates}
        onRetry={() => loadCandidates(candidates.interval)}
        onCall={handleCall}
        onDismiss={setDismissTarget}
      />

      <ContactDrawer
        open={Boolean(contactCandidate)}
        onClose={handleCloseContact}
        interval={candidates.interval}
        candidate={contactCandidate}
        stepMinutes={stepMinutes}
        compose={compose}
        onRecompose={(time, minutes) => requestCompose(contactCandidate, time, minutes)}
        sending={sending}
        error={contactError}
        onSend={handleSend}
      />

      <ConfirmPopup
        Visibility={Boolean(dismissTarget)}
        Title="Agora não"
        Description={dismissTarget ? `${dismissTarget.name} deixa de aparecer nas sugestões por 30 dias. Continuar?` : ''}
        ConfirmLabel="Agora não"
        OnConfirm={handleConfirmDismiss}
        OnHide={() => setDismissTarget(null)}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'success'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.text}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default RecoveryPage;
